import {
  CompanyActivity,
  ContractType,
  DocumentType,
  EmploymentStatus,
  ExtractorDocumentType,
  ExtractorMaritalStatus,
  Gender,
  MaritalStatus,
  NetIncomeType,
  ProfessionalPosition,
  ResidenceOwnership,
  ResidenceProperty,
} from '@nemuru-eng/js-api-client';

import { BorrowerOnboardingDetailsFormType } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/PaymentMethods/PaymentMethod/Loan/Loan/PaymentIntent/Multilender/MerchantPos/MerchantPosForm/components/BorrowerOnboardingDetailsForm/schemas/borrowerOnboardingDetailsForm.schema';
import { ExtractionResult } from '@/components/shared/components/DocumentExtractorDialog/contexts/DocumentExtractorContext';
import { GeocodingFunctions } from '@/components/shared/components/DocumentExtractorDialog/DocumentExtractorDialog';
import { geocoderToAddressFieldsMapper } from '@/components/shared/components/NewContextForms/components/AutocompleteAddressHeadless/components/GoogleMapsAutocomplete/mappers/geocoderToAddressFieldsMapper';
import { AddressSchemaType } from '@/components/shared/components/NewContextForms/components/AutocompleteAddressHeadless/schemas/AddressSchema';

export class DocumentExtractorMap {
  static async toFormValues(
    results: ExtractionResult[],
    geocoding: GeocodingFunctions,
  ): Promise<Partial<BorrowerOnboardingDetailsFormType>> {
    const values = {} as Partial<BorrowerOnboardingDetailsFormType>;

    const put = <K extends keyof BorrowerOnboardingDetailsFormType>(
      key: K,
      value: BorrowerOnboardingDetailsFormType[K] | null | undefined,
    ) => {
      if (value !== null && value !== undefined) {
        values[key] = value;
      }
    };

    for (const result of results) {
      if (!result.success || !result.extractedData) {
        continue;
      }

      if (result.documentType === ExtractorDocumentType.DniFront) {
        put('firstName', str(result.extractedData.firstName));
        put('lastName', str(result.extractedData.lastName));
        put('documentNumber', str(result.extractedData.documentNumber));
        put('gender', result.extractedData.sex);
        put('birthDate', date(result.extractedData.birthDate));
        put('expiration', date(result.extractedData.expiryDate));
        put('nationality', str(result.extractedData.nationality));
        put('documentExpeditor', 'ESP');
        put('documentType', DocumentType.NIF);
      }

      if (result.documentType === ExtractorDocumentType.DniBack) {
        put('birthPlace', str(result.extractedData.birthPlace));
        if (result.extractedData.address) {
          const documentAddress = await geocodeAddress(String(result.extractedData.address), geocoding);
          if (documentAddress) put('documentAddress', documentAddress);
          if (documentAddress) put('mainResidenceAddress', documentAddress);
        }
      }

      if (result.documentType === ExtractorDocumentType.DniFrontBack) {
        const { front, back } = result.extractedData;

        put('firstName', str(front.firstName));
        put('lastName', str(front.lastName));
        put('documentNumber', str(front.documentNumber));
        put('gender', front.sex);
        put('birthDate', date(front.birthDate));
        put('expiration', date(front.expiryDate));
        put('nationality', str(front.nationality));
        put('documentExpeditor', 'ESP');
        put('documentType', DocumentType.NIF);

        if (back) {
          put('birthPlace', str(back.birthPlace));
          if (back.address) {
            const documentAddress = await geocodeAddress(String(back.address), geocoding);
            if (documentAddress) put('documentAddress', documentAddress);
            if (documentAddress) put('mainResidenceAddress', documentAddress);
          }
        }
      }

      if (result.documentType === ExtractorDocumentType.NieFront) {
        put('firstName', str(result.extractedData.firstName));
        put('lastName', str(result.extractedData.lastName));
        put('documentNumber', str(result.extractedData.documentNumber));
        put('birthDate', date(result.extractedData.birthDate));
        put('expiration', date(result.extractedData.expiryDate));
        put('nationality', str(result.extractedData.nationality));
        put('documentExpeditor', 'ESP');
        put('documentType', DocumentType.NIE_EU);
        put('gender', result.extractedData.sex);
      }

      if (result.documentType === ExtractorDocumentType.NieBack) {
        put('birthPlace', str(result.extractedData.birthPlace));
        if (result.extractedData.address) {
          const documentAddress = await geocodeAddress(String(result.extractedData.address), geocoding);
          if (documentAddress) put('documentAddress', documentAddress);
          if (documentAddress) put('mainResidenceAddress', documentAddress);
        }
      }

      if (result.documentType === ExtractorDocumentType.NieEuGreenCard) {
        put('firstName', str(result.extractedData.firstName));
        put('lastName', str(result.extractedData.lastName));
        put('documentNumber', str(result.extractedData.documentNumber));
        put('birthDate', date(result.extractedData.birthDate));
        put('nationality', str(result.extractedData.nationality));
        put('documentExpeditor', 'ESP');
        put('documentType', DocumentType.NIE_EU);
        if (result.extractedData.address) {
          const documentAddress = await geocodeAddress(String(result.extractedData.address), geocoding);
          if (documentAddress) put('documentAddress', documentAddress);
          if (documentAddress) put('mainResidenceAddress', documentAddress);
        }
      }

      if (result.documentType === ExtractorDocumentType.BankCertificate) {
        put('bankAccountNumber', str(result.extractedData.iban));
        continue;
      }

      if (result.documentType === ExtractorDocumentType.Payroll) {
        put('employmentStatus', mapEmploymentStatus(result.extractedData.contractTypeDescription));
        put('monthlyNetIncome', str(num(result.extractedData.netIncome)));
        put('numberOfYearlyPayments', num(result.extractedData.numberOfPayments));
        put('companyName', str(result.extractedData.companyName));
        put('companyCity', str(result.extractedData.companyCity));
        put('corporateIdNumber', str(result.extractedData.companyCif));
        put('companyActivity', str(result.extractedData.companyActivity) as CompanyActivity);
        put('seniority', date(result.extractedData.seniorityDate));
        put('professionalPosition', str(result.extractedData.professionalPosition) as ProfessionalPosition);
        put('netIncomeType', NetIncomeType.SALARY);
      }

      if (result.documentType === ExtractorDocumentType.PersonalIncomeTax) {
        put('firstName', str(result.extractedData.taxpayer?.firstName));
        put('lastName', str(result.extractedData.taxpayer?.lastName));
        put('gender', result.extractedData.taxpayer?.sex as Gender);
        put('birthDate', date(result.extractedData.taxpayer?.birthDate));
        put('maritalStatus', mapMaritalStatus(result.extractedData.taxpayer?.maritalStatus));
        put('documentExpeditor', 'ESP');
        put('documentType', DocumentType.NIF);
        put('documentNumber', str(result.extractedData.taxpayer?.nif));
        if (result.extractedData.family?.dependents?.length) {
          put('childSupportNumber', num(result.extractedData.family.dependents.length));
        }

        if (result.extractedData.workIncome?.netIncome) {
          put('monthlyNetIncome', str(num(result.extractedData.workIncome.netIncome)! / 12));
          put('numberOfYearlyPayments', num(12));
        }

        if (result.extractedData.realEstate?.length) {
          for (const realEstate of result.extractedData.realEstate) {
            if (realEstate.habitualResidence) {
              const realEstateAddress = await geocodeAddress(String(realEstate.address), geocoding);
              if (realEstateAddress) put('mainResidenceAddress', realEstateAddress);
            }
            if (realEstate.habitualResidence && realEstate.owner === 'DECLARANTE') {
              put('ownershipType', ResidenceOwnership.OWNERSHIP);
              put('propertyType', ResidenceProperty.PUBLIC_DEED);
            }
          }
        }
        continue;
      }

      if (result.documentType === ExtractorDocumentType.PensionRevaluation) {
        put('employmentStatus', EmploymentStatus.PENSIONER);
        put('monthlyNetIncome', str(num(result.extractedData.monthlyNetIncome)));
        put('numberOfYearlyPayments', num(result.extractedData.numberOfPayments));
        put('netIncomeType', NetIncomeType.PENSION);
        continue;
      }

      // if (docType === 'pension_revaluation') {
      //   put('monthlyNetIncome', str(num(d.monthlyNetIncome)));
      //   put('numberOfYearlyPayments', num(d.numberOfPayments));
      //   put('netIncomeType', NetIncomeType.PENSION);
      //   continue;
      // }
    }

    return values;
  }
}

const parseDate = (dateStr: string): Date | null => {
  const d = new Date(dateStr);
  return isNaN(d.getTime()) ? null : d;
};

const str = (val: unknown): string | null => (val != null ? String(val) : null);
const num = (val: unknown): number | null => (val != null && val !== '' ? Number(val) : null);
const date = (val: unknown): Date | null => (val != null ? parseDate(String(val)) : null);

const geocodeAddress = async (
  rawAddress: string,
  { retrievePredictions, geocodePlace }: GeocodingFunctions,
): Promise<AddressSchemaType | null> => {
  try {
    const predictions = await retrievePredictions(rawAddress);
    if (!predictions.length) return null;
    const geocode = await geocodePlace(predictions[0].place_id);
    const fields = geocoderToAddressFieldsMapper(geocode);
    return {
      address: geocode.formatted_address,
      streetName: fields.streetName,
      streetNumber: fields.streetNumber,
      postalCode: fields.postalCode,
      city: fields.city ?? '',
      province: fields.province,
      provinceShortName: fields.provinceShortName,
      region: fields.region,
      regionShortName: fields.regionShortName,
      country: fields.country,
      countryLongName: fields.countryLongName,
      latitude: fields.latitude as number,
      longitude: fields.longitude as number,
    };
  } catch (error) {
    return null;
  }
};

const mapEmploymentStatus = (type?: ContractType | null): EmploymentStatus => {
  if (!type) return EmploymentStatus.OTHER;
  if (type.startsWith('indefinite')) return EmploymentStatus.INDEFINITE_EMPLOYED;
  if (type.startsWith('temporary') || type.startsWith('permanent')) return EmploymentStatus.TEMPORARY_EMPLOYED;
  if (type.startsWith('training')) return EmploymentStatus.TRAINEE;
  if (type.startsWith('public')) return EmploymentStatus.CIVIL_SERVANT;
  return EmploymentStatus.OTHER;
};

const mapMaritalStatus = (status?: ExtractorMaritalStatus | null): MaritalStatus | null => {
  if (!status) return null;
  if (status === ExtractorMaritalStatus.MARRIED) return MaritalStatus.MARRIED;
  if (status === ExtractorMaritalStatus.DIVORCED) return MaritalStatus.DIVORCED;
  if (status === ExtractorMaritalStatus.WIDOWED) return MaritalStatus.WIDOWED;
  if (status === ExtractorMaritalStatus.SINGLE) return MaritalStatus.SINGLE;
  return null;
};
